'use client';

import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Icon from '@/components/ui/AppIcon';

export interface ServicePricingTier {
  id: string;
  qty: string;
  qtyNum: number;
  price: number;
  delivery: string;
  popular?: boolean;
  savings?: string;
}

interface ServicePageTemplateProps {
  platform: 'facebook' | 'tiktok' | 'instagram' | 'youtube';
  serviceType: 'followers' | 'likes' | 'views' | 'comments';
  title: string;
  subtitle: string;
  description: string;
  seoKeywords: string[];
  heroEmoji: string;
  gradient: string;
  badgeColor: string;
  tiers: ServicePricingTier[];
  whatsappNumber?: string;
}

const WHATSAPP_NUMBER = '2250575956671';

function formatPrice(price: number): string {
  return price.toLocaleString('fr-FR') + ' XOF';
}

export default function ServicePageTemplate({
  platform,
  serviceType,
  title,
  subtitle,
  description,
  seoKeywords,
  heroEmoji,
  gradient,
  badgeColor,
  tiers,
  whatsappNumber = WHATSAPP_NUMBER,
}: ServicePageTemplateProps) {
  const platformLabel = platform === 'facebook' ? 'Facebook' : platform === 'tiktok' ? 'TikTok' : platform === 'instagram' ? 'Instagram' : 'YouTube';
  const platformEmoji = platform === 'facebook' ? '📘' : platform === 'tiktok' ? '🎵' : platform === 'instagram' ? '📸' : '▶️';

  const [selectedTier, setSelectedTier] = useState<ServicePricingTier | null>(null);
  const [gmail, setGmail] = useState('');
  const [profileLink, setProfileLink] = useState('');
  const [errors, setErrors] = useState<{ gmail?: string; profileLink?: string }>({});

  const profileLinkLabel =
    platform === 'facebook' ? 'Lien de votre page / profil Facebook'
      : platform === 'tiktok' ? 'Lien de votre profil TikTok'
      : platform === 'youtube'? 'Lien de votre chaîne / vidéo YouTube' :'Lien de votre profil Instagram';

  const profileLinkPlaceholder =
    platform === 'facebook' ? 'https://facebook.com/votre-page'
      : platform === 'tiktok' ? 'https://tiktok.com/@votre-compte'
      : platform === 'youtube' ? 'https://youtube.com/@votre-chaine'
      : 'https://instagram.com/votre-compte';

  const validate = () => {
    const newErrors: { gmail?: string; profileLink?: string } = {};
    if (!gmail.trim()) {
      newErrors.gmail = 'Veuillez entrer votre adresse Gmail.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gmail.trim())) {
      newErrors.gmail = 'Adresse email invalide.';
    }
    if (!profileLink.trim()) {
      newErrors.profileLink = 'Veuillez entrer le lien de votre profil.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (tier: ServicePricingTier) => {
    setSelectedTier(tier);
    setGmail('');
    setProfileLink('');
    setErrors({});
  };

  const handleCloseModal = () => {
    setSelectedTier(null);
    setGmail('');
    setProfileLink('');
    setErrors({});
  };

  const handleSendWhatsApp = () => {
    if (!selectedTier) return;
    if (!validate()) return;

    const message = encodeURIComponent(
      `Bonjour ! Je souhaite commander :\n\n` +
      `📦 Service : ${title}\n` +
      `📊 Quantité : ${selectedTier.qty}\n` +
      `💰 Prix : ${formatPrice(selectedTier.price)}\n` +
      `⏱️ Livraison : ${selectedTier.delivery}\n\n` +
      `📧 Email (Gmail) : ${gmail.trim()}\n` +
      `🔗 Lien du profil : ${profileLink.trim()}\n\n` +
      `Merci de confirmer ma commande.`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    handleCloseModal();
  };

  const isDark = platform === 'tiktok';
  const isInstagram = platform === 'instagram';
  const isYoutube = platform === 'youtube';

  return (
    <main>
      <div className="grain-overlay" aria-hidden="true" />
      <Header />

      {/* Hero Section */}
      <section
        className="pt-28 pb-16 px-4 sm:px-6 relative overflow-hidden"
        style={{ background: gradient }}
      >
        {/* Decorative blobs */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle,rgba(244,169,35,0.12) 0%,transparent 70%)',
            transform: 'translate(30%,-30%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle,rgba(255,255,255,0.04) 0%,transparent 70%)',
            transform: 'translate(-30%,30%)',
          }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/50 text-xs mb-6 flex-wrap">
            <Link href="/homepage" className="hover:text-white transition-colors">Accueil</Link>
            <Icon name="ChevronRightIcon" size={12} className="text-white/30" />
            <Link href="/services" className="hover:text-white transition-colors">Services</Link>
            <Icon name="ChevronRightIcon" size={12} className="text-white/30" />
            <span className="text-white/80">{title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              {/* Badge */}
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5"
                style={{ background: 'rgba(244,169,35,0.15)', border: '1px solid rgba(244,169,35,0.3)' }}
              >
                <span className="w-2 h-2 rounded-full bg-[#F4A923] animate-pulse" />
                <span className="text-[#F4A923] text-xs font-bold tracking-widest uppercase">
                  {platformEmoji} {platformLabel} · Livraison garantie
                </span>
              </div>

              <h1
                className="font-heading font-black text-white mb-4"
                style={{ fontSize: 'clamp(1.8rem,4.5vw,3rem)', lineHeight: 1.1 }}
              >
                {heroEmoji} {title}
                <br />
                <span
                  style={{
                    background: 'linear-gradient(135deg,#F4A923,#F7C05A)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {subtitle}
                </span>
              </h1>

              <p className="text-white/70 mb-6 max-w-lg" style={{ fontSize: 15, lineHeight: 1.7 }}>
                {description}
              </p>

              {/* SEO Keywords as tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {seoKeywords.map((kw) => (
                  <span
                    key={kw}
                    className="px-3 py-1 rounded-full text-xs font-semibold text-white/80"
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
                  >
                    {kw}
                  </span>
                ))}
              </div>

              {/* Quick stats */}
              <div className="flex flex-wrap gap-5">
                {[
                  { n: '12 500+', l: 'Commandes' },
                  { n: '< 24h', l: 'Livraison' },
                  { n: '4.9★', l: 'Note' },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="text-white font-heading font-bold text-xl">{s.n}</div>
                    <div className="text-white/45 text-xs">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Trust badges */}
            <div className="hidden lg:block">
              <div
                className="rounded-3xl p-7"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <div className="text-white/60 text-xs font-bold uppercase tracking-wider mb-4">
                  ✅ Pourquoi nous choisir ?
                </div>
                <div className="space-y-3">
                  {[
                    { emoji: '🔒', label: 'Sans mot de passe requis', desc: 'Votre compte reste sécurisé' },
                    { emoji: '⚡', label: 'Livraison rapide', desc: 'Démarrage en moins de 1h' },
                    { emoji: '🛡️', label: 'Garantie 30 jours', desc: 'Remplacement gratuit si baisse' },
                    { emoji: '💬', label: 'Support WhatsApp 24/7', desc: 'Réponse en moins de 30 min' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 p-3 rounded-2xl"
                      style={{ background: 'rgba(255,255,255,0.06)' }}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.1)' }}
                      >
                        {item.emoji}
                      </div>
                      <div>
                        <div className="text-white text-sm font-semibold">{item.label}</div>
                        <div className="text-white/40 text-xs">{item.desc}</div>
                      </div>
                      <Icon name="CheckCircleIcon" size={18} variant="solid" className="ml-auto text-[#10B981]" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none" style={{ height: 50 }}>
          <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
            <path d="M0 50 L0 25 Q360 0 720 25 Q1080 50 1440 25 L1440 50 Z" fill={isDark ? '#0a0a0a' : isInstagram ? '#1a0a1a' : '#F4F6FB'} />
          </svg>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        className="py-16 px-4 sm:px-6"
        style={{
          background: isDark
            ? 'linear-gradient(135deg,#0a0a0a 0%,#0a0a1a 100%)'
            : isInstagram
            ? 'linear-gradient(135deg,#1a0a1a 0%,#0d0a1a 100%)'
            : '#F4F6FB',
        }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2
              className="font-heading font-black mb-3"
              style={{
                fontSize: 'clamp(1.5rem,3vw,2.2rem)',
                color: isDark || isInstagram ? 'white' : '#0D1B3E',
              }}
            >
              Choisissez votre pack
            </h2>
            <p style={{ color: isDark || isInstagram ? 'rgba(255,255,255,0.6)' : '#6B7280', fontSize: 15 }}>
              Paiement sécurisé · Orange Money · MTN MoMo · Wave · Carte bancaire
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className="relative rounded-2xl p-5 transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: tier.popular
                    ? gradient
                    : isDark || isInstagram
                    ? 'rgba(255,255,255,0.05)'
                    : 'white',
                  border: tier.popular
                    ? `2px solid ${badgeColor}`
                    : isDark || isInstagram
                    ? '1px solid rgba(255,255,255,0.1)'
                    : '1px solid rgba(13,27,62,0.08)',
                  boxShadow: tier.popular ? `0 8px 32px ${badgeColor}33` : '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                {tier.popular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white"
                    style={{ background: badgeColor, whiteSpace: 'nowrap' }}
                  >
                    ⭐ Populaire
                  </div>
                )}
                {tier.savings && !tier.popular && (
                  <div
                    className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-bold text-white"
                    style={{ background: '#10B981', whiteSpace: 'nowrap' }}
                  >
                    {tier.savings}
                  </div>
                )}

                <div className="mb-3">
                  <div
                    className="font-heading font-black text-xl mb-1"
                    style={{ color: tier.popular || isDark || isInstagram ? 'white' : '#0D1B3E' }}
                  >
                    {tier.qty}
                  </div>
                  <div
                    className="text-2xl font-black font-heading"
                    style={{ color: tier.popular ? '#F4A923' : badgeColor }}
                  >
                    {formatPrice(tier.price)}
                  </div>
                </div>

                <div
                  className="flex items-center gap-1.5 text-xs mb-4"
                  style={{ color: tier.popular || isDark || isInstagram ? 'rgba(255,255,255,0.6)' : '#6B7280' }}
                >
                  <Icon name="ClockIcon" size={12} />
                  <span>Livraison : {tier.delivery}</span>
                </div>

                <button
                  onClick={() => handleOpenModal(tier)}
                  className="w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 hover:opacity-90 active:scale-95"
                  style={{
                    background: '#25D366',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(37,211,102,0.3)',
                  }}
                >
                  <span>💬</span>
                  <span>Commander sur WhatsApp</span>
                </button>
              </div>
            ))}
          </div>

          {/* Features */}
          <div
            className="mt-10 p-6 rounded-3xl"
            style={{
              background: isDark || isInstagram ? 'rgba(255,255,255,0.05)' : 'white',
              border: isDark || isInstagram ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(13,27,62,0.06)',
            }}
          >
            <h3
              className="font-heading font-bold text-sm mb-4 flex items-center gap-2"
              style={{ color: isDark || isInstagram ? 'white' : '#0D1B3E' }}
            >
              <Icon name="ShieldCheckIcon" size={16} className="text-[#10B981]" />
              Tous nos packs incluent
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { icon: '✅', text: 'Profils réels & actifs' },
                { icon: '🔒', text: 'Sans mot de passe' },
                { icon: '⚡', text: 'Livraison automatique' },
                { icon: '🛡️', text: 'Garantie 30 jours' },
                { icon: '💬', text: 'Support WhatsApp' },
                { icon: '🔄', text: 'Remplacement gratuit' },
              ].map((f) => (
                <div
                  key={f.text}
                  className="flex items-center gap-2 p-3 rounded-xl text-xs font-medium"
                  style={{
                    background: isDark || isInstagram ? 'rgba(255,255,255,0.06)' : 'rgba(244,246,251,0.8)',
                    color: isDark || isInstagram ? 'rgba(255,255,255,0.8)' : '#374151',
                  }}
                >
                  <span>{f.icon}</span>
                  <span>{f.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Back to services */}
          <div className="mt-8 text-center">
            <Link
              href="/services"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:opacity-80"
              style={{
                background: isDark || isInstagram ? 'rgba(255,255,255,0.1)' : 'rgba(13,27,62,0.06)',
                color: isDark || isInstagram ? 'white' : '#0D1B3E',
                border: isDark || isInstagram ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(13,27,62,0.1)',
              }}
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Voir tous les services
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Order Modal */}
      {selectedTier && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}
        >
          <div
            className="w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
            style={{ background: '#0D1B3E', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            {/* Modal Header */}
            <div
              className="px-6 py-5 flex items-center justify-between"
              style={{ background: gradient, borderBottom: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div>
                <div className="text-white font-heading font-black text-lg leading-tight">
                  💬 Commander sur WhatsApp
                </div>
                <div className="text-white/70 text-xs mt-0.5">
                  {selectedTier.qty} · {formatPrice(selectedTier.price)}
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
              >
                <Icon name="XMarkIcon" size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 space-y-5">
              <p className="text-white/60 text-sm leading-relaxed">
                Remplissez les champs ci-dessous. Votre commande sera envoyée directement sur WhatsApp.
              </p>

              {/* Gmail Field */}
              <div>
                <label className="block text-white/80 text-xs font-bold mb-2 uppercase tracking-wider">
                  📧 Adresse Gmail
                </label>
                <input
                  type="email"
                  value={gmail}
                  onChange={(e) => { setGmail(e.target.value); setErrors((prev) => ({ ...prev, gmail: undefined })); }}
                  placeholder="exemple@gmail.com"
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: errors.gmail ? '1.5px solid #EF4444' : '1.5px solid rgba(255,255,255,0.12)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = `1.5px solid ${badgeColor}`; }}
                  onBlur={(e) => { e.currentTarget.style.border = errors.gmail ? '1.5px solid #EF4444' : '1.5px solid rgba(255,255,255,0.12)'; }}
                />
                {errors.gmail && (
                  <p className="text-red-400 text-xs mt-1.5">{errors.gmail}</p>
                )}
              </div>

              {/* Profile Link Field */}
              <div>
                <label className="block text-white/80 text-xs font-bold mb-2 uppercase tracking-wider">
                  🔗 {profileLinkLabel}
                </label>
                <input
                  type="url"
                  value={profileLink}
                  onChange={(e) => { setProfileLink(e.target.value); setErrors((prev) => ({ ...prev, profileLink: undefined })); }}
                  placeholder={profileLinkPlaceholder}
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: errors.profileLink ? '1.5px solid #EF4444' : '1.5px solid rgba(255,255,255,0.12)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = `1.5px solid ${badgeColor}`; }}
                  onBlur={(e) => { e.currentTarget.style.border = errors.profileLink ? '1.5px solid #EF4444' : '1.5px solid rgba(255,255,255,0.12)'; }}
                />
                {errors.profileLink && (
                  <p className="text-red-400 text-xs mt-1.5">{errors.profileLink}</p>
                )}
              </div>

              {/* Order Summary */}
              <div
                className="rounded-2xl p-4 space-y-2"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="text-white/50 text-xs font-bold uppercase tracking-wider mb-3">Récapitulatif</div>
                {[
                  { label: 'Service', value: title },
                  { label: 'Quantité', value: selectedTier.qty },
                  { label: 'Prix', value: formatPrice(selectedTier.price) },
                  { label: 'Livraison', value: selectedTier.delivery },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-center text-sm">
                    <span className="text-white/50">{row.label}</span>
                    <span className="text-white font-semibold">{row.value}</span>
                  </div>
                ))}
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendWhatsApp}
                className="w-full py-3.5 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95"
                style={{
                  background: 'linear-gradient(135deg,#25D366,#128C7E)',
                  boxShadow: '0 6px 20px rgba(37,211,102,0.35)',
                }}
              >
                <span style={{ fontSize: 18 }}>💬</span>
                <span>Envoyer ma commande sur WhatsApp</span>
              </button>

              <p className="text-white/30 text-xs text-center">
                Vous serez redirigé vers WhatsApp avec votre commande pré-remplie.
              </p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
